(ns dre.widgets.dates.events
  (:require
   [dre.fx :as dre.fx]
   ["moment" :as moment]
   [clojure.string :as str]
   [day8.re-frame.tracing :refer-macros [fn-traced]]
   [dre.i18n.utils :as i18n-utils]
   [dre.network :as-alias network]
   [dre.pages.search.events :as search-events]
   [dre.utils :refer [stringify-keys-with-namespaces aborted-request?]]
   [goog.string :as gstring]
   [re-frame.core :as rf]
   [superstructor.re-frame.fetch-fx :as-alias fetch-fx]))


(def db-path [:widgets :dates])

(def id :dates)

(defn yyyy
  [date]
  (.. (.utc moment date)
      (format "YYYY")))

(defn yyyyfd
  [date]
  (str (yyyy date) "-01-01"))

(defn yyyyld
  [date]
  (str (yyyy date) "-12-31"))


(defn ->pill
  [field val]
  {:pill/type :and
   :pill/pills [{:pill/type :range
                 :pill/field field
                 :pill/start (yyyyfd (first val))}
                {:pill/type :range
                 :pill/field field
                 :pill/end (yyyyld (second val))}]})

(defn result
  [response]
  (let [{:keys [:distribution]} response
        end-m (gstring/format "%02d-%02d"
                              (.getFullYear (js/Date.))
                              (.getMonth (js/Date.)))
        dk-y1 (gstring/format "%02d-%02d"
                              (dec (.getFullYear (js/Date.)))
                              (inc (.getMonth (js/Date.))))
        dk-y2 (gstring/format "%02d-%02d"
                              (- (.getFullYear (js/Date.)) 2)
                              (inc (.getMonth (js/Date.))))
        dk-y5 (gstring/format "%02d-%02d"
                              (- (.getFullYear (js/Date.)) 5)
                              (inc (.getMonth (js/Date.))))
        mmf (fn [year [month m]]
              {:year (name year) :month (name month)
               :count (:count m)
               :name (str (name year) "-" (name month))})
        ymf (fn [[year months]]
              (map #(mmf year %) months))
        aymf (fn [[year months]]
               (let [cnt (reduce + (map (comp :count second)
                                        months))]
                 {:year (name year)
                  :count cnt
                  :name (name year)}))
        months (sort-by :name #(compare %2 %)
                        (mapcat ymf distribution))
        years (sort-by :name #(compare %2 %)
                       (map aymf distribution))
        months (filter #(< (compare (:name %) end-m) 0) months)
        y1 (filter #(nat-int? (compare (:name %) dk-y1)) months)
        y2 (filter #(nat-int? (compare (:name %) dk-y2)) months)
        y5 (filter #(nat-int? (compare (:name %) dk-y5)) months)]
    {:years years
     :months months
     :y1 y1
     :y2 y2
     :y5 y5}))


;; -- Events

(rf/reg-event-fx
 ::set-loading
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} _]
  {:db (-> db
           (assoc-in [:state :published] :loading)
           (assoc-in [:state :changed] :loading))}))



(rf/reg-event-fx
 ::init-published
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [after-fx last-selected-filter-key pills-api-params attempt]}]]
  (let [params (merge
                pills-api-params
                {:query/field :published
                 :response/filter
                 {:distribution true
                  :done? true}})]
    {:db (assoc-in db [:state :published] :loading)
     :dispatch-later {:ms (+ 300 (min 6000 (* 300 (or attempt 0))))
                      :dispatch [::network/request-with-abort {:endpoint :widgets/dates-published}
                                 {:body (stringify-keys-with-namespaces params)
                                  :on-success [::success-published {:after-fx after-fx :last-selected-filter-key last-selected-filter-key
                                                                    :pills-api-params pills-api-params :attempt attempt}]
                                  :on-failure [::failure {:after-fx after-fx}]}]}})))

(rf/reg-event-fx
 ::init-changed
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [after-fx last-selected-filter-key pills-api-params attempt]}]]
  (let [params (merge
                pills-api-params
                {:query/field :changed
                 :response/filter
                 {:distribution true
                  :done? true}})]
    {:db (assoc-in db [:state :changed] :loading)
     :dispatch-later {:ms (+ 300 (min 6000 (* 300 (or attempt 0))))
                      :dispatch [::network/request-with-abort {:endpoint :widgets/dates-changed}
                                 {:body (stringify-keys-with-namespaces params)
                                  :on-success [::success-changed {:after-fx after-fx :last-selected-filter-key last-selected-filter-key
                                                                  :pills-api-params pills-api-params :attempt attempt}]
                                  :on-failure [::failure {:after-fx after-fx}]}]}})))

(rf/reg-event-fx
 ::init
 [(rf/path db-path) rf/trim-v]
 (fn
  [{db :db} [{:keys [after-fx last-selected-filter-key pills-api-params-fn]}]]
  (let [pills-api-params (pills-api-params-fn [id])
        new-db (if (= last-selected-filter-key (str id :published))
                 (assoc-in db [:state :published] nil)
                 (-> db
                     (assoc-in [:data :published] nil)
                     (assoc-in [:state :published] :loading)
                     (assoc-in [:done? :published] nil)))
        new-db (if (= last-selected-filter-key (str id :changed))
                 (assoc-in new-db [:state :changed] nil)
                 (-> new-db
                     (assoc-in [:data :changed] nil)
                     (assoc-in [:state :changed] :loading)
                     (assoc-in [:done? :changed] nil)))]
    {:db new-db
     :fx [(when (not= last-selected-filter-key (str id :published))
            [:dispatch [::init-published {:after-fx after-fx :last-selected-filter-key last-selected-filter-key
                                          :pills-api-params pills-api-params :attempt 0}]])
          (when (not= last-selected-filter-key (str id :changed))
            [:dispatch [::init-changed {:after-fx after-fx :last-selected-filter-key last-selected-filter-key
                                        :pills-api-params pills-api-params :attempt 0}]])]})))


(rf/reg-event-fx
  ::success-published
  [(rf/path db-path) rf/trim-v]
  (fn-traced
   [{db :db} [{:keys [after-fx last-selected-filter-key pills-api-params attempt ::fetch-fx/response]}]]
   (let [done? (:done? (:body response))
         data (:body response)
         fx (if (and (not done?) (> 10 attempt))
              [::init-published {:after-fx after-fx :last-selected-filter-key last-selected-filter-key
                                 :pills-api-params pills-api-params :attempt (inc attempt)}]
              after-fx)]
     {:db (-> db
              (assoc-in [:data :published] (result data))
              (assoc-in [:state :published] :success)
              (assoc-in [:done? :published] done?))
      :dispatch fx})))

(rf/reg-event-fx
 ::success-changed
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [after-fx last-selected-filter-key pills-api-params attempt ::fetch-fx/response]}]]
  (let [done? (:done? (:body response))
        data (:body response)
        fx (if (and (not done?) (> 10 attempt))
             [::init-changed {:after-fx after-fx :last-selected-filter-key last-selected-filter-key
                              :pills-api-params pills-api-params :attempt (inc attempt)}]
             after-fx)]
    {:db (-> db
             (assoc-in [:data :changed] (result data))
             (assoc-in [:state :changed] :success)
             (assoc-in [:done? :changed] done?))
     :dispatch fx})))

(rf/reg-event-fx
  ::failure
  [(rf/path db-path) rf/trim-v]
  (fn-traced
    [_ [{:keys [after-fx ::fetch-fx/response] :as opts}]]
    (if (aborted-request? response)
      {}
      {:db {:state {:published :failure
                    :changed :failure}
            :data {:published nil
                   :changed nil}}
       :dispatch-n [after-fx
                    [:dre.network/default-failure (merge {:event :dates} opts)]]})))


(rf/reg-event-fx
 ::export
 [rf/trim-v]
 (fn-traced
  [cofx [field]]
  (let [root-db (:db cofx)
        i18n-db (:i18n root-db)
        db (get-in root-db db-path)
        data (get-in db [:data field :years])
        data (sort-by :year < data)
        header (str (i18n-utils/translate i18n-db "Year") ", "
                    (i18n-utils/translate i18n-db "count"))
        lines (map (fn [{:keys [:year :count]}]
                     (str year "," count)) data)
        csv (str/join "\n" (cons header lines))]
    {::dre.fx/download {:content csv
                        :filename (str "dates_" (name field) ".csv")
                        :mimetype "text/csv"}})))

(rf/reg-event-fx
 ::set-extent-selection
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [field extent]]
  (let []
    {:db (-> db
             (assoc-in [:data field :extent-selection] extent))})))

;; -- Subscriptions

(rf/reg-sub
  ::state
  (fn [db _]
    (get-in db db-path)))

(rf/reg-sub
 ::done?
 :<- [::state]
 (fn [db [_ field]]
   (get-in db [:done? field])))

(rf/reg-sub
 ::status
 :<- [::state]
 (fn [state [_ field]]
   (get-in state [:state field])))


(rf/reg-sub
 ::data
 :<- [::state]
 (fn [state [_ field]]
   (get-in state [:data field])))

(rf/reg-sub
 ::extent-selection
 :<- [::state]
 (fn [state [_ field]]
   (get-in state [:data field :extent-selection])))

;;

(defn- published-through-pill?
  [{:pill/keys [field end]}]
  (and
   (= field :published)
   (some? end)))

(defn published-through-filters
  [db]
  (let [f (->> db
               search-events/db-path
               :widget-filters)
        published-f (get f (str id :published))]
    (->> published-f
         (mapcat :pill/pills)
         (filter published-through-pill?))))

(defn published-through-queries
  [db]
  (->> db
       search-events/db-path
       :search-query
       :query/pills
       (filter published-through-pill?)))

(defn- dates-pill?
  [{:pill/keys [field end]}]
  (or
   (= field :changed)
   (= field :published)))

(defn dates-filters
  [db]
  (let [f (->> db
               search-events/db-path
               :widget-filters)
        published-f (get f (str id :published))
        changed-f (get f (str id :changed))]
    (->> (concat published-f changed-f)
         (mapcat :pill/pills)
         (filter dates-pill?))))

(defn dates-queries
  [db]
  (->> db
       search-events/db-path
       :search-query
       :query/pills
       (filter dates-pill?)))

(defn selected-dates?
  [db]
  (let [x (concat (dates-queries db) (dates-filters db))]
    (not-empty x)))

(rf/reg-sub
 ::used-in-search?
 selected-dates?)